import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
  SagaCallbacks,
} from '@gymcloud/core';

import {
  getEntityType,
} from 'web-utils';
import {
  getFeatures,
} from 'web-selectors';

import {
  Spinner,
  NestedWorkout,
  WorkoutTemplate,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

type Props = {
  id?: number,
  workout: any,
  basePath: string,
  parentType: 'workoutTemplates',
  resourceType: 'cooldowns' | 'warmups',
};

const AdditionalWorkoutPage = ({
  id,
  workout,
  basePath,
  parentType,
  resourceType,
}: Props) => {
  const navigate = useNavigate();
  const mapState = useCallback(state => ({
    status: getStatus(
      state,
      [
        `${resourceType}.requests.getFullAdditionalWorkout.status`,
        `${resourceType}.meta[${id}].readStatus`,
      ],
      true,
    ),
    options: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
    features: getFeatures(state),
    folderRoots: coreSelectors.getFolderRoots(state),
    additionalWorkout: id ? coreSelectors.getResourceById(
      resourceType,
      id,
    )(state) : undefined,
  }), [resourceType, id]);

  const {
    status,
    options,
    features,
    folderRoots,
    additionalWorkout,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      dispatch(
        coreActions.requestFullWorkout({
          id,
          resourceType: parentType,
          addtionalType: resourceType,
        }),
      );
    }
  }, [id]);

  const type = getEntityType(resourceType);
  const fillPath = `${basePath}/${workout.id}/${type}`;

  const handleAdd = (payload: any, callbacks: SagaCallbacks = {}) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: workout.id,
        payload,
        resourceType: parentType,
        callbacks,
      }),
    );
  };

  const handleSave = () => {
    if (!id) return;
    dispatch(
      coreActions.requestDuplicateEntity({
        ids: [id],
        folderIds: [type === 'warmup'
          ? folderRoots.warmupId
          : folderRoots.cooldownId,
        ],
        resourceType,
      }),
    );
  };

  const handleSubmit = (payload: any, actions: SagaCallbacks) => {
    if (!id) return;
    dispatch(
      coreActions.requestUpdateEntity({
        id,
        payload,
        endNotify: true,
        requestType: parentType,
        resourceType,
        callbacks: {
          ...actions,
          onSuccess: () => {
            navigate(fillPath);
          },
        },
      }),
    );
  };

  const handleDelete = () => {
    const payload = type === 'warmup'
      ? { warmup_id: null }
      : { cooldown_id: null };
    dispatch(
      coreActions.requestUpdateEntity({
        id: workout.id,
        payload,
        resourceType: parentType,
      }),
    );
  };

  return id ? (
    <Flex fillSpace>
      {status.succeeded && (
        <Routes>
          <Route
            index
            element={(
              <WorkoutTemplate
                readOnly
                type={type}
                workout={additionalWorkout}
                fillPath={fillPath}
                deleteText="Remove"
                resourceType={resourceType}
                onRequestSave={handleSave}
                onRequestSubmit={() => {}}
                onRequestDelete={handleDelete}
              />
            )}
          />
          <Route
            path="edit"
            element={(
              <WorkoutTemplate
                type={type}
                workout={additionalWorkout}
                fillPath={fillPath}
                features={features}
                resourceType={resourceType}
                onRequestSubmit={handleSubmit}
              />
            )}
          />
        </Routes>
      )}

      {status.pending && (
        <Spinner message="Loading..." />
      )}
    </Flex>
  ) : (
    <NestedWorkout
      type={type}
      options={options}
      onRequestAdd={({ value }) => {
        handleAdd(type === 'warmup'
          ? { warmup_id: value }
          : { cooldown_id: value });
      }}
      onRequestAddNew={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: '0' }
          : { cooldown_id: '0' };

        const callbacks = value !== 'new' ? {
          onSuccess: (result: any) => {
            dispatch(
              coreActions.requestUpdateEntity({
                id: type === 'warmup' ? result.warmup_id : result.cooldown_id,
                payload: { name: value },
                resourceType,
              }),
            );
          },
        } : {};

        handleAdd(payload, callbacks);
      }}
    />
  );
};

AdditionalWorkoutPage.defaultProps = {
  id: undefined,
};

export default AdditionalWorkoutPage;
